import { useState, useEffect } from "react";
import {
  Menu,
  X,
  ShoppingCart,
  Search,
  ChevronDown,
  ChevronRight,
  User,
  Heart,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import canonique_epice from "../../Images/canonique-epice.jpg";
import { fetchUserLikes, toggleLike } from "../../redux/likesSlice";
// Couleurs du drapeau du Niger
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};
function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function KassarmouHomepage() {
  const DATA_Products = useSelector((state) => state.products.data);
  const navigate = useNavigate();
  const DATA_Products_pubs = useSelector(
    (state) => state.products.products_Pubs
  );
  const DATA_Categories = useSelector((state) => state.products.categories);
  const [selectedCategory, setSelectedCategory] = useState(1);
  const [bannerIndex, setBannerIndex] = useState(0);

  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("success");
  const [showNotification, setShowNotification] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  function extractTextFromHTML(htmlString) {
    // Crée un élément temporaire pour parser le HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    // Récupère uniquement le texte lisible
    return tempDiv.innerText.trim();
  }

  const dispatch = useDispatch();
  const likedProducts = useSelector((state) => state.likes.likedProducts);
  const userId = JSON.parse(localStorage.getItem("userEcomme"))?.id;

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserLikes(userId));
    }
  }, [userId, dispatch]);

  const showToast = (message, type = "success") => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  // Rotation automatique des bannières
  useEffect(() => {
    const interval = setInterval(() => {
      setBannerIndex(
        (prevIndex) => (prevIndex + 1) % DATA_Products_pubs?.length
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [DATA_Products_pubs]);

  // Filtrage des produits par catégorie
  const filteredProducts =
    selectedCategory === 1
      ? DATA_Products
      : DATA_Products?.filter(
        (product) => product.ClefCategorie === selectedCategory
      );

  // Calcul du pourcentage de réduction
  const calculateDiscount = (originalPrice, promoPrice) => {
    return Math.round(((originalPrice - promoPrice) / originalPrice) * 100);
  };

  const handleLikeClick = async (product, e) => {
    e.stopPropagation();

    if (!userId) {
      showToast("Veuillez vous connecter pour ajouter des favoris", "error");
      return;
    }

    try {
      await dispatch(toggleLike({ userId, product })).unwrap();
      showToast(
        likedProducts.includes(product._id)
          ? "Produit retiré des favoris"
          : "Produit ajouté aux favoris"
      );
    } catch (error) {
      showToast("Une erreur est survenue", "error");
      console.error("Erreur:", error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Main Content */}
      {showNotification && (
        <div
          className={cn(
            "fixed top-4 right-4 z-50 px-4 py-3 rounded shadow-lg transition-all duration-300",
            notificationType === "success"
              ? "bg-green-100 border-green-400 text-green-700"
              : "bg-red-100 border-red-400 text-red-700"
          )}
        >
          <p className="text-sm">{notificationMessage}</p>
        </div>
      )}
      <main className="flex-grow">
        {/* Banner Section */}
        <section className="relative h-[220px] sm:h-[240px] md:h-[260px] lg:h-[280px] overflow-hidden">
          <div
            className="absolute inset-0 flex items-center transition-all duration-700 ease-in-out"
            style={{
              background: `linear-gradient(135deg, ${COLORS[bannerIndex] || '#f3f4f6'} 0%, ${COLORS.lightGreen} 50%, #ffffff 100%)`,
            }}
          >
            {/* Mobile Background Image with improved overlay */}
            <div className="absolute inset-0 md:hidden z-0">
              {DATA_Products_pubs && (
                <img
                  src={DATA_Products_pubs[bannerIndex]?.image || ""}
                  alt="Bannière background"
                  className="w-full h-full object-cover"
                />
              )}
              {/* Modern gradient overlay for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
            </div>


            <div className="container mx-auto relative z-10 h-full flex items-center px-6 md:px-10 lg:px-16">
              <div className="flex flex-col md:flex-row items-center gap-8 lg:gap-16 w-full">

                {/* Contenu textuel amélioré */}
                <div className="w-full md:w-1/2 text-center md:text-left space-y-2 md:space-y-3 animate-fade-in">
                  <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-yellow-300 md:text-gray-900 leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                    {DATA_Products_pubs?.[bannerIndex]?.title || ""}
                  </h2>
                  
                  <p className="text-xs sm:text-sm md:text-base text-gray-100 md:text-gray-600 max-w-xl mx-auto md:mx-0 leading-relaxed line-clamp-2">
                    {DATA_Products_pubs?.[bannerIndex]?.description || ""}
                  </p>
                </div>

                {/* Image avec animation améliorée (Desktop Only) */}
                <div className="hidden md:flex w-full md:w-1/2 h-full items-center justify-center">
                  {DATA_Products_pubs && (
                    <div className="relative transform transition-all duration-700 hover:scale-105 group">
                      {/* Decorative circle background */}
                      <div className="absolute inset-0 bg-white/20 rounded-full blur-2xl scale-125 group-hover:scale-150 transition-transform duration-700"></div>
                      
                      <img
                        src={DATA_Products_pubs[bannerIndex]?.image || ""}
                        alt="Bannière promotionnelle"
                        className="relative rounded-xl shadow-xl w-auto max-h-[200px] lg:max-h-[220px] object-contain transform group-hover:rotate-1 transition-all duration-500"
                        loading="eager"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contrôles de navigation améliorés */}
          <div className="absolute bottom-6 left-0 right-0 flex justify-center items-center space-x-3 z-20">
            {DATA_Products_pubs?.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setBannerIndex(idx)}
                aria-label={`Voir la bannière ${idx + 1}`}
                className={`transition-all duration-300 transform ${bannerIndex === idx
                  ? "w-12 h-3 bg-orange-500 shadow-lg scale-110"
                  : "w-3 h-3 bg-white/70 hover:bg-white hover:scale-125"
                  } rounded-full`}
              />
            ))}
          </div>

          {/* Flèches de navigation modernisées */}
          {DATA_Products_pubs && DATA_Products_pubs.length > 1 && (
            <>
              <button
                onClick={() =>
                  setBannerIndex((prev) =>
                    prev === 0 ? DATA_Products_pubs.length - 1 : prev - 1
                  )
                }
                className="absolute left-4 sm:left-6 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white text-gray-800 p-3 sm:p-4 rounded-full shadow-xl hover:shadow-2xl transition-all hover:scale-110 z-20 hidden sm:flex items-center justify-center"
                aria-label="Bannière précédente"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={() =>
                  setBannerIndex((prev) =>
                    prev === DATA_Products_pubs.length - 1 ? 0 : prev + 1
                  )
                }
                className="absolute right-4 sm:right-6 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white text-gray-800 p-3 sm:p-4 rounded-full shadow-xl hover:shadow-2xl transition-all hover:scale-110 z-20 hidden sm:flex items-center justify-center"
                aria-label="Bannière suivante"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}
        </section>

        {/* Category filter */}
        <section className="py-4 bg-white sticky top-0 z-30 shadow-sm">
          <div className="container mx-auto px-4">
            <h2 className="text-lg font-bold mb-3 text-gray-800">
              Nos produits du Niger
            </h2>

            <div className="relative group">
              <div className="flex overflow-x-auto whitespace-nowrap gap-3 pb-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
                {[
                  {
                    _id: 1,
                    name: "Tous",
                  },
                  ...(Array.isArray(DATA_Categories) ? DATA_Categories : []),
                ]?.map((category) => (
                  <button
                    key={category?._id}
                    onClick={() => setSelectedCategory(category?._id)}
                    className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${selectedCategory === category
                      ? "text-white shadow-md transform scale-105"
                      : "text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100"
                      }`}
                    style={{
                      backgroundColor:
                        selectedCategory === category ? COLORS.green : "",
                      borderColor:
                        selectedCategory === category ? COLORS.green : "",
                    }}
                  >
                    {category?.name}
                  </button>
                ))}
              </div>
              {/* Scroll Indicator Fade */}
              <div className="absolute right-0 top-0 bottom-2 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none sm:hidden"></div>
            </div>

            {/* Products grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {filteredProducts?.slice(0, 9).map((product) => (
                <div
                  key={product._id}
                  className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:scale-105"
                >
                  <div
                    className="relative aspect-square overflow-hidden"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    <img
                      onClick={() => navigate(`/ProduitDétail/${product?._id}`)}
                      src={product?.image1 || product?.image}
                      alt={product?.name}
                      className="object-cover w-full h-full transform transition-transform duration-500"
                      style={{
                        transform: isHovered ? "scale(1.1)" : "scale(1.0)",
                      }}
                    />
                    {/* Badge de promotion si applicable */}
                    {product.prixPromo &&
                      Number(product.prixPromo) > 0 &&
                      Number(product.prixPromo) < Number(product.prix) && (
                        <div
                          className="absolute top-2 left-2 px-2 py-1 rounded-full text-white text-xs font-bold"
                          style={{ backgroundColor: COLORS.orange }}
                        >
                          -
                          {calculateDiscount(
                            Number(product.prix),
                            Number(product.prixPromo)
                          )}
                          %
                        </div>
                      )}

                    <button
                      onClick={(e) => handleLikeClick(product, e)}
                      className={cn(
                        "absolute top-3 left-3 p-2 rounded-full shadow-lg transition-all duration-300 z-20",
                        likedProducts.includes(product._id)
                          ? "bg-red-50 hover:bg-red-100"
                          : "bg-white hover:bg-emerald-50"
                      )}
                    >
                      <Heart
                        className={cn(
                          "w-5 h-5 transition-colors duration-300",
                          likedProducts.includes(product._id)
                            ? "text-red-500 fill-red-500"
                            : "text-emerald-600"
                        )}
                      />
                    </button>
                  </div>
                  <div className="p-4">
                    <span
                      className="text-xs font-medium px-2 py-1 rounded-full text-white inline-block mb-2"
                      style={{ backgroundColor: COLORS.green }}
                    >
                      {DATA_Categories?.find(
                        (cat) => cat._id === product.ClefCategorie
                      )?.name || "Catégorie"}
                    </span>
                    <h3 className="font-bold text-lg mb-1">{product?.name}</h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {/* Essaie d'extraire le texte brut de la description HTML si nécessaire */}
                      {extractTextFromHTML(product.description)}
                    </p>
                    <div className="flex justify-between items-center">
                      {/* Affichage du prix avec réduction si applicable */}
                      <div>
                        {product.prixPromo &&
                          Number(product.prixPromo) > 0 &&
                          Number(product.prixPromo) < Number(product.prix) ? (
                          <div>
                            <span
                              className="font-bold text-lg mr-2"
                              style={{ color: COLORS.orange }}
                            >
                              {Number(product.prixPromo).toLocaleString()} €
                            </span>
                            <span className="text-gray-500 text-sm line-through">
                              {Number(product.prix).toLocaleString()} €
                            </span>
                          </div>
                        ) : (
                          <span
                            className="font-bold text-lg"
                            style={{ color: COLORS.orange }}
                          >
                            {Number(product.prix).toLocaleString()} €
                          </span>
                        )}
                      </div>
                      <button
                        className="px-3 py-1 rounded-full text-sm text-white font-medium flex items-center"
                        style={{ backgroundColor: COLORS.green }}
                        onClick={() =>
                          navigate(`/ProduitDétail/${product?._id}`)
                        }
                      >
                        <ShoppingCart size={16} className="mr-1" />
                        Ajouter
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* View all button */}
            <div className="text-center mt-8">
              <a
                onClick={() => navigate(`/produits`)}
                className="inline-flex items-center px-6 py-3 rounded-full font-medium text-white hover:shadow-lg transition-shadow"
                style={{ backgroundColor: COLORS.orange }}
              >
                Voir tous les produits{" "}
                <ChevronRight size={20} className="ml-1" />
              </a>
            </div>
          </div>
        </section>

        {/* Features section */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div
                  className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: COLORS.lightGreen }}
                >
                  {/* Placeholder for icon */}
                  <div
                    className="w-8 h-8 bg-green-600 rounded-full"
                    style={{ backgroundColor: COLORS.green }}
                  ></div>
                </div>
                <h3 className="font-bold text-lg mb-2">
                  Artisanat authentique
                </h3>
                <p className="text-gray-600">
                  Produits fabriqués à la main par des artisans locaux du Niger.
                </p>
              </div>

              <div className="text-center p-4">
                <div
                  className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: COLORS.lightOrange }}
                >
                  {/* Placeholder for icon */}
                  <div
                    className="w-8 h-8 bg-orange-500 rounded-full"
                    style={{ backgroundColor: COLORS.orange }}
                  ></div>
                </div>
                <h3 className="font-bold text-lg mb-2">Livraison rapide et sécurisée en Europe</h3>
                <p className="text-gray-600">
                  Nous livrons vos commandes partout en Europe avec efficacité et fiabilité. Profitez d’un service logistique de qualité, sans compromis sur la rapidité ni la sécurité.
                </p>
              </div>

              <div className="text-center p-4">
                <div
                  className="bg-green-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: COLORS.paleGreen }}
                >
                  {/* Placeholder for icon */}
                  <div
                    className="w-8 h-8 bg-green-700 rounded-full"
                    style={{ backgroundColor: COLORS.darkGreen }}
                  ></div>
                </div>
                <h3 className="font-bold text-lg mb-2">Commerce équitable</h3>
                <p className="text-gray-600">
                  Nous soutenons les communautés locales avec des prix justes.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Advertisement Banner */}
        <section className="py-8 bg-gray-50">
          <div className="container mx-auto px-4">
            <div
              className="rounded-xl overflow-hidden shadow-lg"
              style={{ backgroundColor: COLORS.lightOrange }}
            >
              <div className="p-6 md:p-8 flex flex-col md:flex-row items-center">
                <div className="md:w-2/3 mb-6 md:mb-0 md:pr-8">
                  <h2 className="text-2xl md:text-3xl font-bold mb-4">
                    Découvrez nos nouvelles collections
                  </h2>
                  <p className="text-gray-700 mb-6">
                    Explorez notre sélection d'articles exclusifs directement
                    importés du Niger. Chaque achat soutient les artisans
                    locaux.
                  </p>
                  <button
                    className="px-6 py-3 rounded-full font-medium text-white shadow-md hover:shadow-lg transition-shadow"
                    style={{ backgroundColor: COLORS.orange }}
                    onClick={() => navigate(`/produits`)}
                  >
                    En savoir plus
                  </button>
                </div>
                <div className="md:w-1/3">
                  <img
                    src={canonique_epice}
                    alt="Produits du Niger"
                    className="rounded-lg shadow-md"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
